import React, { useState, useEffect, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LandingNavbar from "../landing/LandingNavbar";
import Footer from "../landing/Footer";
import FinalCTA from "../landing/FinalCTA";
import useGlobalAccessGrantActive from "../landing/useGlobalAccessGrantActive";
import { loadArticleBySlug, getNextArticle } from "./utils/articleLoader";
import { AuthContext } from '../../core/context/AuthContext';
import { PrimaryButton, SecondaryButton, LoadingSpinner } from "../../design-system";
import { ChevronLeftIcon, ChevronRightIcon, UserProfileIcon, CalendarIcon } from "../../design-system/icons";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const isFreeGrantActive = useGlobalAccessGrantActive();
  const [article, setArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const loadedArticle = await loadArticleBySlug(slug);
        if (!loadedArticle) {
          setError('Article not found');
        } else {
          setArticle(loadedArticle);
          // Get the next article
          const next = await getNextArticle(slug);
          setNextArticle(next);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading article:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
        <LandingNavbar />
        <div className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <LoadingSpinner size="xl" color="primary" />
            </div>
            <p className={textColors.secondary}>Loading article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
        <LandingNavbar />
        <div className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className={`${typography.h2} ${textColors.primary} mb-4`}>
              {error === 'Article not found' ? 'Article Not Found' : 'Error Loading Article'}
            </h1>
            <p className={`${typography.body} ${textColors.secondary} mb-6`}>
              {error === 'Article not found' 
                ? 'The article you\'re looking for doesn\'t exist or has been moved.'
                : `Error: ${error}`
              }
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/blog">
                <PrimaryButton>
                  Back to Blog
                </PrimaryButton>
              </Link>
              <SecondaryButton onClick={() => window.location.reload()}>
                Try Again
              </SecondaryButton>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
      <LandingNavbar />
      
      <div className="flex-1 pt-16">
        {/* Brand Gradient Header Section with Back Button */}
        <div className={`bg-gradient-to-r ${brandColors.gradientFrom} ${brandColors.gradientTo} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="mb-6">
              <Link 
                to="/blog"
                className="inline-flex items-center text-blue-100 dark:text-blue-100 hover:text-white transition-colors duration-200 font-medium"
              >
                <ChevronLeftIcon size="sm" className="mr-2" />
                Back to Articles
              </Link>
            </div>
            
            <div className="text-center">
              <h1 className={`${typography.h1Dark} mb-4`}>
                {article.title}
              </h1>
              
              <div className={`${typography.bodySmallDark} flex items-center justify-center space-x-6`}>
                <div className="flex items-center">
                  <UserProfileIcon size="sm" className="mr-2" />
                  <span className="font-medium">{article.author}</span>
                </div>
                
                <div className="flex items-center">
                  <CalendarIcon size="sm" className="mr-2" />
                  <time>{formatDate(article.date)}</time>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className={`${surfaces.primary} rounded-lg shadow-md p-8`}>
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({children}) => <h1 className={`font-heading text-3xl font-bold ${textColors.primary} mb-6 mt-8 first:mt-0`}>{children}</h1>,
                  h2: ({children}) => <h2 className={`font-heading text-2xl font-bold ${textColors.primary} mb-4 mt-8`}>{children}</h2>,
                  h3: ({children}) => <h3 className={`font-heading text-xl font-bold ${textColors.primary} mb-3 mt-6`}>{children}</h3>,
                  h4: ({children}) => <h4 className={`font-heading text-lg font-bold ${textColors.primary} mb-2 mt-4`}>{children}</h4>,
                  p: ({children}) => <p className={`${textColors.secondary} leading-relaxed mb-4`}>{children}</p>,
                  ul: ({children}) => <ul className={`list-disc list-inside ${textColors.secondary} mb-4 space-y-1`}>{children}</ul>,
                  ol: ({children}) => <ol className={`list-decimal list-inside ${textColors.secondary} mb-4 space-y-1`}>{children}</ol>,
                  li: ({children}) => <li className="mb-1">{children}</li>,
                  blockquote: ({children}) => (
                    <blockquote className={`border-l-4 ${brandColors.primaryBorder} pl-4 py-2 mb-4 ${brandColors.primaryLight} ${textColors.primary} italic`}>
                      {children}
                    </blockquote>
                  ),
                  code: ({ node, inline, className, children, ...props }) => {
                    const baseInlineClasses = `${surfaces.tertiary} ${textColors.brightest} px-1 py-0.5 rounded text-sm font-mono`;
                    const baseBlockClasses = `${textColors.brightest} text-sm font-mono`;
                    const mergedClassName = `${inline ? baseInlineClasses : baseBlockClasses}${className ? ` ${className}` : ''}`;

                    return (
                      <code className={mergedClassName} {...props}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({ node, className, children, ...props }) => (
                    <pre
                      className={`${surfaces.tertiary} ${borders.default} border p-4 rounded-lg overflow-x-auto mb-4${className ? ` ${className}` : ''}`}
                      {...props}
                    >
                      {children}
                    </pre>
                  ),
                  a: ({children, href, ...props}) => (
                    <a 
                      href={href} 
                      className={`${brandColors.primary} ${brandColors.primaryHover} underline`}
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                  strong: ({children}) => <strong className={`font-bold ${textColors.primary}`}>{children}</strong>,
                  em: ({children}) => <em className={`italic ${textColors.primary}`}>{children}</em>,
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>

            {/* Article Footer with Navigation */}
            <div className={`mt-12 pt-8 border-t ${borders.default}`}>
              <div className="flex items-center justify-between">
                <Link 
                  to="/blog"
                  className={`inline-flex items-center ${typography.link} text-xs sm:text-sm`}
                >
                  <ChevronLeftIcon size="xs" className="mr-1 sm:mr-2" />
                  Back to Articles
                </Link>
                
                <div className={`${typography.muted} ${textColors.muted} text-center flex-1 px-2`}>
                  Published on {formatDate(article.date)}
                </div>
                
                <Link 
                  to={`/blog/${nextArticle.slug}`}
                  className={`inline-flex items-center ${typography.link} text-xs sm:text-sm`}
                >
                  Next Article
                  <ChevronRightIcon size="xs" className="ml-1 sm:ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Show FinalCTA for non-authenticated users */}
      {!auth && <FinalCTA isFreeGrantActive={isFreeGrantActive} />}

      <Footer />
    </div>
  );
};

export default BlogArticle;